/**
 * ComposerPicker - compact card-style single-select for the composer toolbar.
 *
 * A trigger button shows the current selection; clicking opens a portaled
 * Radix Popover card of options (label + optional description, checkmark on the
 * active one). The Portal escapes the composer card's `overflow: hidden`, and
 * Radix collision handling flips the card above/below to fit the viewport.
 *
 * Generic + reusable: drives both the model selector and the Ask/Agent/Auto
 * mode picker (and any future composer control) - see CLAUDE.md "Reusable
 * primitives".
 */

import { Pill, Popover } from '@protoss/ui';
import { CheckIcon } from '@radix-ui/react-icons';
import * as React from 'react';

// Single source of the stage->pill map lives in lib/featureStage; re-exported here so existing
// importers (SlashCommandMenu) keep their path while the definition stays in one place.
import { STAGE_PILL_VARIANT } from '../../../lib/featureStage';

export { STAGE_PILL_VARIANT };

export interface ComposerPickerOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  /**
   * Feature maturity stage gating this option (absent = 'ga', always shown). Consumers
   * filter the options list with `isStageAvailable(stage, workspace.featureMinStage)` so
   * alpha-marked options appear only in local/dev. The picker itself renders whatever it
   * is handed - gating happens at the call site.
   */
  stage?: 'alpha' | 'beta';
}

export interface ComposerPickerProps<T extends string> {
  value: T;
  options: ReadonlyArray<ComposerPickerOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel: string;
  /** Shown in the trigger when no option matches `value` (e.g. models still loading). */
  placeholder?: string;
}

export function ComposerPicker<T extends string>({
  value,
  options,
  onChange,
  disabled,
  ariaLabel,
  placeholder,
}: ComposerPickerProps<T>) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="ai-chat-composer-picker"
          disabled={disabled}
          aria-label={ariaLabel}
        >
          <span className="ai-chat-composer-picker__value">
            {selected?.label ?? placeholder ?? value}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="ai-chat-composer-picker__card"
          align="start"
          side="top"
          sideOffset={6}
          collisionPadding={8}
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={
                option.value === value
                  ? 'ai-chat-composer-picker__option ai-chat-composer-picker__option--active'
                  : 'ai-chat-composer-picker__option'
              }
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span className="ai-chat-composer-picker__option-check" aria-hidden="true">
                {option.value === value ? <CheckIcon /> : null}
              </span>
              <span className="ai-chat-composer-picker__option-main">
                <span className="ai-chat-composer-picker__option-label-row">
                  <span className="ai-chat-composer-picker__option-label">{option.label}</span>
                  {option.stage ? (
                    <Pill
                      variant={STAGE_PILL_VARIANT[option.stage]}
                      className="ai-chat-composer-picker__stage"
                    >
                      {option.stage}
                    </Pill>
                  ) : null}
                </span>
                {option.description ? (
                  <span className="ai-chat-composer-picker__option-description">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
